import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RelativeUrl } from "@adora/shared/utils/environment";
import post from "@adora/shared/utils/post";
import fetcher from "@adora/shared/utils/fetch";
import type { JsonValue } from "@adora/shared/utils/patch";

interface OnboardingUser {
  first_name: string;
  last_name: string;
  email: string;
  share_link?: string;
  username?: string;
  email_sent?: boolean;
}

interface BrandOnboardingStepStatus {
  step_create_brand: string;
  step_iac_pr: string;
  step_iac_merge: string;
  step_set_s3_bucket: string;
  step_delorean_pr: string;
  step_create_users: string;
  step_brand_dna: string;
}

export interface BrandOnboardingResponse {
  id: number;
  brand_id: number | null;
  brand_name: string;
  brand_slug: string;
  brand_description: string | null;
  brand_website_url: string | null;
  status: string;
  steps: BrandOnboardingStepStatus;
  iac_pr_url: string | null;
  delorean_pr_url: string | null;
  error_message: string | null;
  users: OnboardingUser[];
}

interface CreateBrandOnboardingRequest {
  brand_name: string;
  brand_slug: string;
  brand_description: string;
  brand_website_url: string;
  users: OnboardingUser[];
}

const ONBOARDING_URL = `${RelativeUrl}/brand-onboarding`;

export const useBrandOnboardingList = () => {
  return useQuery<BrandOnboardingResponse[]>({
    queryKey: ["brand-onboardings"],
    queryFn: () => fetcher<BrandOnboardingResponse[]>(ONBOARDING_URL)
  });
};

export const useBrandOnboarding = (id: number | null) => {
  return useQuery<BrandOnboardingResponse>({
    queryKey: ["brand-onboarding", id],
    queryFn: () => fetcher<BrandOnboardingResponse>(`${ONBOARDING_URL}/${id}`),
    enabled: id !== null,
    refetchInterval: 10_000 // Poll every 10s for status updates
  });
};

export const useCreateBrandOnboarding = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateBrandOnboardingRequest) => {
      const result = await post<BrandOnboardingResponse>(ONBOARDING_URL, {}, request as unknown as JsonValue);
      return result.match(
        (error) => {
          throw new Error(error.message);
        },
        (data) => data
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brand-onboardings"] });
    }
  });
};

export const useAdvanceOnboarding = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (onboardingId: number) => {
      const result = await post<BrandOnboardingResponse>(`${ONBOARDING_URL}/${onboardingId}/advance`, {});
      return result.match(
        (error) => {
          throw new Error(error.message);
        },
        (data) => data
      );
    },
    onSuccess: (_, onboardingId) => {
      queryClient.invalidateQueries({ queryKey: ["brand-onboarding", onboardingId] });
      queryClient.invalidateQueries({ queryKey: ["brand-onboardings"] });
    }
  });
};

export const useSendWelcomeEmails = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (onboardingId: number) => {
      const result = await post<BrandOnboardingResponse>(`${ONBOARDING_URL}/${onboardingId}/send-emails`, {});
      return result.match(
        (error) => {
          throw new Error(error.message);
        },
        (data) => data
      );
    },
    onSuccess: (_, onboardingId) => {
      queryClient.invalidateQueries({ queryKey: ["brand-onboarding", onboardingId] });
    }
  });
};
